import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AppContainer, MainContentContainer, BodyContainer, ProdButton } from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { CircularProgress, Chip, Stack, IconButton, Checkbox, FormControlLabel, TablePagination, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Cookies from 'js-cookie';
import schoolService from '../../services/api/schoolService';
import WondeConnectionPanel from '../../components/common/WondeConnectionPanel';
import {
  impersonate,
  ScopeType,
  getCallbackUrlForProduct
} from '../../utils/impersonateUtils';
import { Product, PRODUCT_DISPLAY_NAMES, EI_TIER_PRODUCT_IDS } from '../../utils/Constants';
import superAdminService from '../../services/api/superAdminService';
import { DateFormat } from '../../utils/CommonFunctions';
import SeatCard from '../../components/common/SeatCard';
import AccountTabs from '../../components/account/AccountTabs';
import AiUsageSection from '../../components/aiUsage/AiUsageSection';
import UserManagementSection from '../../components/userManagement/UserManagementSection';
import { sa, Card, DataTable, PillButton, SectionHeaderRow, SelectPill } from '../../components/superAdminUi';

// Presentation for the Super Admin School account view — the account header cards and the
// Survey Info tab — rendered on the shared Super Admin design system so it matches the approved
// Figma. Data fetching, filters, pagination, impersonation and modals are untouched.

const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  font-family: ${sa.font.family};
`;

const AccountGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: ${sa.space.section};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SchoolName = styled.h2`
  margin: 0;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 22px;
  color: ${sa.color.text};
`;

const DetailList = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.textGray};
`;

const DetailLabel = styled.span`
  color: ${sa.color.textGray};
`;

const DetailValue = styled.strong`
  color: ${sa.color.text};
  font-weight: ${sa.font.semibold};
`;

const SubscriptionCard = styled(Card)`
  background: #E8F6FC;
  border-color: #53B5E0;
`;

const CardTitle = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 16px;
  color: ${sa.color.text};
`;

const EditLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 13px;
  color: ${sa.color.teal};

  &:hover {
    text-decoration: underline;
  }
`;

const SubBlock = styled.div`
  margin-top: 14px;
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.text};

  p {
    margin: 0 0 10px;
  }
`;

const Muted = styled.span`
  color: ${sa.color.textGray};
`;

const DateStart = styled.span`
  color: #f2732a;
  font-weight: ${sa.font.semibold};
`;

const DateEnd = styled.span`
  color: ${sa.color.teal};
  font-weight: ${sa.font.semibold};
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.span`
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 300px;
  padding: 7px 12px;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
  }

  svg {
    color: ${sa.color.textGray};
    font-size: 18px;
  }
`;

const HeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${sa.color.white};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: inherit;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${sa.radius.sm};
  background: ${sa.color.tabInactive};
  color: ${sa.color.textGray};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 12px;
  text-transform: capitalize;
`;

const PaginationRow = styled.div`
  margin-top: 4px;

  .MuiTablePagination-toolbar {
    padding-left: 0;
  }
`;

const checkboxSx = {
  color: sa.color.stroke,
  '&.Mui-checked': { color: sa.color.teal },
};

const checkboxLabelSx = {
  m: 0,
  '& .MuiFormControlLabel-label': {
    fontFamily: sa.font.family,
    fontSize: 13,
    color: sa.color.text,
  },
};

const surveyStatusOptions = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Draft' },
  { value: 'closed', label: 'Closed' },
  { value: 'published', label: 'Published' },
];

const surveyAudienceOptions = [
  { value: 0, label: 'All' },
  { value: 1, label: 'Staff' },
  { value: 2, label: 'Parents' },
  { value: 3, label: 'Staff & Parents' },
  { value: 4, label: 'Pupils' },
  { value: 5, label: 'Staff & Students' },
  { value: 6, label: 'Parents & Students' },
  { value: 7, label: 'Staff, Parents & Students' },
];

// Static billing demo rows (unchanged content, restyled into the shared DataTable).
const billingRows = [
  { id: 'new-account', orderDate: 'Sun Jun 14 2022', pupils: 100, generatedFrom: 'New Account', totalPrice: 247, discount: '5%' },
  { id: 'renew-account', orderDate: 'Sun Jun 14 2022', pupils: 100, generatedFrom: 'Renew Account', totalPrice: 247, discount: '5%' },
];

// Render the audience chip(s) for a survey row — same audience→chip mapping as before.
const renderSurveyAudience = (row) => {
  const audienceValue = row.audience !== undefined ? row.audience : (row.Audience !== undefined ? row.Audience : null);
  const audience = audienceValue !== null && audienceValue !== undefined
    ? (typeof audienceValue === 'string' ? parseInt(audienceValue, 10) : audienceValue)
    : null;

  return (
    <Stack direction='row' spacing={0.5}>
      {audience === 1 ? (
        <Chip label='Staff' className='staff-chip' size='small' />
      ) : audience === 2 ? (
        <Chip label='Parent' className='parent-chip' size='small' />
      ) : audience === 3 ? (
        <>
          <Chip label='Staff' className='staff-chip' size='small' />
          <Chip label='Parent' className='parent-chip' size='small' />
        </>
      ) : audience === 4 ? (
        <Chip label='Student' className='student-chip' size='small' />
      ) : audience === 5 ? (
        <>
          <Chip label='Staff' className='staff-chip' size='small' />
          <Chip label='Student' className='student-chip' size='small' />
        </>
      ) : audience === 6 ? (
        <>
          <Chip label='Parent' className='parent-chip' size='small' />
          <Chip label='Student' className='student-chip' size='small' />
        </>
      ) : audience === 7 ? (
        <>
          <Chip label='Staff' className='staff-chip' size='small' />
          <Chip label='Parent' className='parent-chip' size='small' />
          <Chip label='Student' className='student-chip' size='small' />
        </>
      ) : (
        <Chip label={audienceValue !== null && audienceValue !== undefined ? `Audience ${audienceValue}` : 'Unknown'} size='small' />
      )}
    </Stack>
  );
};

function SchoolDetail() {
  const navigate = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [adminDashboardInfo, setAdminDashboardInfo] = useState({});
  const [selectedSchoolDetails, setSchoolDetails] = useState({}); // School details object
  const [impersonateLoading, setImpersonateLoading] = React.useState({});

  // Get schoolId from query params, fallback to cookie/localStorage for backward compatibility
  const queryParams = new URLSearchParams(location.search);
  const schoolId = queryParams.get('schoolId') || Cookies.get("SchoolDetailId") || localStorage.getItem("SelectedSchoolId");

  // User Story 7.1: Combined Surveys Table - State for Insights surveys
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [surveysPage, setSurveysPage] = useState(0);
  const [surveysRowsPerPage, setSurveysRowsPerPage] = useState(10);
  const [surveysTotalCount, setSurveysTotalCount] = useState(0);
  const [surveysOrder, setSurveysOrder] = useState('desc');
  const [surveysOrderBy, setSurveysOrderBy] = useState('startDate');
  const [surveysSearchTerm, setSurveysSearchTerm] = useState('');
  const [surveysStatusFilter, setSurveysStatusFilter] = useState('all');
  const [surveysAudienceFilter, setSurveysAudienceFilter] = useState(0);
  const [surveysCheckInFilter, setSurveysCheckInFilter] = useState(false);
  const [surveysShowTesting, setSurveysShowTesting] = useState(false);


  useEffect(() => {
      if (!schoolId) {
        setLoading(false);
        return;
      }
      setLoading(true)
      schoolService.getInfo(schoolId)
        .then((response) => {
          if (response.success)
          {
            setAdminDashboardInfo(response);
            setSchoolDetails(response.data);
            console.log("final Responce",response.data)
            setLoading(false);
          }
        })
        .catch(err => {})
        .finally(() => setLoading(false));
    }, [schoolId]);

  // Helper function to map status string to status number
  const getStatusNumber = (statusString) => {
    const statusMap = {
      'draft': 1,
      'published': 2,
      'live': 3,
      'running': 3,
      'closed': 4,
      'cancelled': 5,
      'canceled': 5
    };
    return statusMap[statusString?.toLowerCase()] || null;
  };

  // Load surveys with server-side filtering and pagination
  const loadSurveys = (
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    searchTermParam = '',
    statusFilterParam = null,
    showTestingSurveysParam = null,
    isCheckInFilterParam = null,
    audienceFilterParam = null
  ) => {
    if (!schoolId || !selectedSchoolDetails.Name) return;

    setSurveysLoading(true);
    const schoolIdNum = parseInt(schoolId, 10) || 0;
    const currentStatusFilter = statusFilterParam !== null ? statusFilterParam : surveysStatusFilter;
    const currentSearchTerm =
      searchTermParam !== undefined && searchTermParam !== null
        ? String(searchTermParam).trim()
        : String(surveysSearchTerm || '').trim();
    const currentAudience =
      audienceFilterParam !== null && audienceFilterParam !== undefined
        ? audienceFilterParam
        : surveysAudienceFilter;

    // Map status filter to statuses array
    const statuses = [];
    if (currentStatusFilter && currentStatusFilter !== 'all' && currentStatusFilter !== '') {
      const statusNumber = getStatusNumber(currentStatusFilter);
      if (statusNumber) {
        statuses.push(statusNumber);
      }
    }

    // Build search params
    const searchParams = {
      schoolId: schoolIdNum,
      schoolSurveysOnly: true,
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    if (currentSearchTerm && currentSearchTerm.length > 0) {
      searchParams.searchTerm = currentSearchTerm;
    }

    if (statuses.length > 0) {
      searchParams.statuses = statuses;
    }

    if (currentAudience && currentAudience > 0) {
      searchParams.audience = currentAudience;
    }

    // Only send check-in filter when checked (true)
    // Use parameter if provided (for immediate updates), otherwise use state
    const currentCheckInFilter = isCheckInFilterParam !== null ? isCheckInFilterParam : surveysCheckInFilter;
    if (currentCheckInFilter) {
      searchParams.isCheckInSurvey = true;
    }

    // Always pass explicit boolean for testing surveys filter
    // Backend handles filtering: true = show only testing, false = exclude testing
    // Use parameter if provided (for immediate updates), otherwise use state
    const currentShowTesting = showTestingSurveysParam !== null ? showTestingSurveysParam : surveysShowTesting;
    searchParams.showTestingSurveys = currentShowTesting;

    superAdminService
      .searchInsightsSurveys(searchParams)
      .then((response) => {
        if (response.success && response.data) {
          // The Insights API (Survey/SearchForSuperAdmin) returns PascalCase fields (Name, Status,
          // Audience, SchoolName, MatName, MatId) with camelCase dates — same shape the dashboard
          // reads. Read PascalCase first (lowercase fallback) so the rows actually populate.
          const mappedData = response.data.map(s => ({
            id: s.Id ?? s.id,
            name: s.Name ?? s.name ?? '',
            startDate: s.startDate ?? s.StartDate,
            endDate: s.endDate ?? s.EndDate,
            status: s.Status ?? s.status ?? '',
            audience: s.Audience ?? s.audience ?? 0,
            schoolName: s.SchoolName ?? s.schoolName ?? '',
            matName: s.MatName ?? s.matName ?? '',
            matId: s.MatId ?? s.matId ?? 0
          }));

          setSurveys(mappedData);
          setSurveysTotalCount(response.totalCount || 0);
        } else {
          setSurveys([]);
          setSurveysTotalCount(0);
        }
      })
      .catch((err) => {
        console.error('Error loading Insights surveys:', err);
        setSurveys([]);
        setSurveysTotalCount(0);
      })
      .finally(() => {
        setSurveysLoading(false);
      });
  };

  useEffect(() => {
    if (!schoolId || !selectedSchoolDetails.Name) return;
    loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, '', 'all');
  }, [schoolId, selectedSchoolDetails.Name]);

  const handleSurveySearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSurveysSearchTerm(newSearchTerm);
    // Only trigger search if 3 or more characters, or if clearing the search (empty)
    if (newSearchTerm.length === 0 || newSearchTerm.length >= 3) {
      setSurveysPage(0);
      loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, newSearchTerm, surveysStatusFilter);
    }
  };

  const handleSurveyStatusFilterChange = (event) => {
    const newStatus = event.target.value;
    setSurveysStatusFilter(newStatus);
    setSurveysPage(0);
    loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, newStatus);
  };

  const handleSurveyAudienceFilterChange = (event) => {
    const newAudience = parseInt(event.target.value, 10) || 0;
    setSurveysAudienceFilter(newAudience);
    setSurveysPage(0);
    // Pass newAudience directly to avoid stale state issue (searchTerm, statusFilter, showTestingSurveys, isCheckIn, audience)
    loadSurveys(
      1,
      surveysRowsPerPage,
      surveysOrderBy,
      surveysOrder,
      surveysSearchTerm,
      surveysStatusFilter,
      null,
      null,
      newAudience
    );
  };

  const handleSurveyCheckInFilterChange = (event) => {
    const newValue = event.target.checked;
    setSurveysCheckInFilter(newValue);
    setSurveysPage(0);
    // Pass newValue directly to avoid stale state issue
    loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter, null, newValue);
  };

  const handleSurveyShowTestingChange = (event) => {
    const newValue = event.target.checked;
    setSurveysShowTesting(newValue);
    setSurveysPage(0);
    // Pass newValue directly to avoid stale state issue (showTestingSurveysParam, isCheckInFilterParam)
    loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter, newValue, null);
  };

  const handleSurveySort = (property) => {
    const isAsc = surveysOrderBy === property && surveysOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSurveysOrder(newOrder);
    setSurveysOrderBy(property);
    setSurveysPage(0);
    loadSurveys(1, surveysRowsPerPage, property, newOrder, surveysSearchTerm, surveysStatusFilter);
  };

  const handleSurveyChangePage = (event, newPage) => {
    setSurveysPage(newPage);
    loadSurveys(newPage + 1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
  };

  const handleSurveyChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setSurveysRowsPerPage(newRowsPerPage);
    setSurveysPage(0);
    loadSurveys(1, newRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
  };

    const formatDate = (isoString) => {
      const date = new Date(isoString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const handleSchoolSubscriptionDetails = (id) => {
        // Handle edit action
        console.log('Edit item with ID:', id);
        navigate.push(`/edit-subscription-school?schoolId=${id}`)
        // You can open a modal or navigate to an edit page here
    };


  // Identity stores no school or group records, so the audit trail can only name the customer a
  // super admin entered if we send the name with the impersonation. Both are already on screen
  // here; when one can't be resolved the API falls back to "<Type> <Id>".
  const auditEntityName = () => selectedSchoolDetails?.Name || '';

    const handleEDUIntellSchoolclick = async (MATId, SchoolId) => {
      const callbackUrl = getCallbackUrlForProduct(Product.EDU_INTELLIGENCE);
      const buttonKey = `edu-school-${SchoolId}`;

      await impersonate({
        entityType: ScopeType.SCHOOL,
        entityId: SchoolId,
        entityName: auditEntityName(),
        groupId: MATId,
        callbackUrl,
        setLoading: (loading) => {
          setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
        },
      productName: PRODUCT_DISPLAY_NAMES[Product.EDU_INTELLIGENCE],
      productType: Product.EDU_INTELLIGENCE,
      });
    };

    const handleSchoolInsightsclick = async (MATId, SchoolId, productId = Product.STUDENT_INSIGHT) => {
      const callbackUrl = getCallbackUrlForProduct(productId);
      const buttonKey = `insights-school-${productId}-${SchoolId}`;

      await impersonate({
        entityType: ScopeType.SCHOOL,
        entityId: SchoolId,
        entityName: auditEntityName(),
        groupId: MATId,
        callbackUrl,
        setLoading: (loading) => {
          setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
        },
      productName: PRODUCT_DISPLAY_NAMES[productId] || 'Insights',
      productType: productId,
      });
    };

    const handleSchoolWelbeingclick = async (MATId, SchoolId, email) => {
      const callbackUrl = getCallbackUrlForProduct(Product.MAIN_APP_TOOLKIT);
      const buttonKey = `wellbeing-school-${SchoolId}`;

      await impersonate({
        entityType: ScopeType.SCHOOL,
        entityId: SchoolId,
        entityName: auditEntityName(),
        groupId: MATId,
        callbackUrl,
        setLoading: (loading) => {
          setImpersonateLoading(prev => ({ ...prev, [buttonKey]: loading }));
        },
      productName: PRODUCT_DISPLAY_NAMES[Product.MAIN_APP_TOOLKIT],
      productType: Product.MAIN_APP_TOOLKIT,
      email,
      });
    };

    // Resolve the displayed Customer Type, tolerating a JSON-encoded array or a plain string.
    const customerTypeLabel = selectedSchoolDetails?.CustomerType
      ? (typeof selectedSchoolDetails.CustomerType === 'string'
        ? (() => {
            try {
              const parsed = JSON.parse(selectedSchoolDetails.CustomerType);
              return Array.isArray(parsed) ? parsed.join(', ') : parsed;
            } catch (e) {
              return selectedSchoolDetails.CustomerType;
            }
          })()
        : (Array.isArray(selectedSchoolDetails.CustomerType)
            ? selectedSchoolDetails.CustomerType.join(', ')
            : selectedSchoolDetails.CustomerType))
      : 'N/A';

    // Product impersonation chips (unchanged behaviour: each is a live impersonation trigger).
    const productChips = (selectedSchoolDetails.schoolProductsList || []).map((marProducts) => (
      <React.Fragment key={marProducts.Id}>
        {marProducts.Product === Product.MAIN_APP_TOOLKIT && (
            <ProdButton
              label="Main App & Toolkit"
              onClick={() => handleSchoolWelbeingclick(
                marProducts.MatGroupId,
                marProducts.Id,
                selectedSchoolDetails?.Email
              )}
              className='staff-chip'
              size='small'
              disabled={impersonateLoading[`wellbeing-school-${marProducts.Id}`]}
            >
              {impersonateLoading[`wellbeing-school-${marProducts.Id}`] ? 'Loading...' : 'Main App & Toolkit'}
            </ProdButton>
        )}
        {marProducts.Product === Product.STUDENT_INSIGHT && (
            <ProdButton
              label="Student Insight"
              onClick={() => handleSchoolInsightsclick(marProducts.MatGroupId,marProducts.Id, Product.STUDENT_INSIGHT)}
              className='parent-chip'
              size='small'
              disabled={impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${marProducts.Id}`]}
            >
              {impersonateLoading[`insights-school-${Product.STUDENT_INSIGHT}-${marProducts.Id}`] ? 'Loading...' : 'Student Insight'}
            </ProdButton>
        )}
        {marProducts.Product === Product.PARENTS_INSIGHTS && (
            <ProdButton
              label="Parents Insights"
              onClick={() => handleSchoolInsightsclick(marProducts.MatGroupId,marProducts.Id, Product.PARENTS_INSIGHTS)}
              className='insight-chip'
              size='small'
              disabled={impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${marProducts.Id}`]}
            >
              {impersonateLoading[`insights-school-${Product.PARENTS_INSIGHTS}-${marProducts.Id}`] ? 'Loading...' : 'Parents Insights'}
            </ProdButton>
        )}
        {marProducts.Product === Product.STAFF_INSIGHTS && (
            <ProdButton
              label="Staff Insights"
              onClick={() => handleSchoolInsightsclick(marProducts.MatGroupId,marProducts.Id, Product.STAFF_INSIGHTS)}
              className='student-chip'
              size='small'
              disabled={impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${marProducts.Id}`]}
            >
              {impersonateLoading[`insights-school-${Product.STAFF_INSIGHTS}-${marProducts.Id}`] ? 'Loading...' : 'Staff Insights'}
            </ProdButton>
        )}
        {EI_TIER_PRODUCT_IDS.includes(marProducts.Product) && (
            <ProdButton
              label={PRODUCT_DISPLAY_NAMES[marProducts.Product]}
              onClick={() => handleEDUIntellSchoolclick(marProducts.MatGroupId,marProducts.Id)}
              className='edu-chip'
              size='small'
              disabled={impersonateLoading[`edu-school-${marProducts.Id}`]}
            >
              {impersonateLoading[`edu-school-${marProducts.Id}`] ? 'Loading...' : PRODUCT_DISPLAY_NAMES[marProducts.Product]}
            </ProdButton>
        )}
        {marProducts.Product === Product.PULSE && (
            <Chip label="Pulse"  className='lparent-chip' size='small'>Pulse</Chip>
        )}
      </React.Fragment>
    ));

    const sub = selectedSchoolDetails.SchoolSubscription;
    const vat = sub?.VatPercentApplied || 0;
    const term = sub?.SubscriptionTerm;
    const yearCostWithVat = (raw) => {
      const base = parseInt(raw || 0);
      return base + (base * (parseInt(vat) / 100));
    };

    // Clickable sortable column header for the surveys DataTable.
    const surveySortHeader = (label, property) => {
      const active = surveysOrderBy === property;
      return (
        <HeaderButton type='button' onClick={() => handleSurveySort(property)}>
          {label}
          {active
            ? (surveysOrder === 'asc'
                ? <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                : <ArrowDownwardIcon sx={{ fontSize: 15 }} />)
            : <SwapVertIcon sx={{ fontSize: 15, opacity: 0.75 }} />}
        </HeaderButton>
      );
    };

    const surveyColumns = [
      {
        key: 'name',
        header: surveySortHeader('Survey Name', 'Name'),
        width: '28%',
        render: (r) => <DetailValue>{r.name || ''}</DetailValue>,
      },
      {
        key: 'startDate',
        header: surveySortHeader('Launch Date', 'startDate'),
        render: (r) => (r.startDate ? DateFormat(r.startDate, 'MM/DD/YYYY') : '-'),
      },
      {
        key: 'endDate',
        header: surveySortHeader('Close Date', 'endDate'),
        render: (r) => (r.endDate ? DateFormat(r.endDate, 'MM/DD/YYYY') : '-'),
      },
      {
        key: 'status',
        header: surveySortHeader('Status', 'Status'),
        render: (r) => (r.status ? <StatusBadge>{r.status}</StatusBadge> : ''),
      },
      {
        key: 'audience',
        label: 'Audience(s)',
        render: renderSurveyAudience,
      },
      {
        key: 'group',
        label: 'Group',
        render: (r) => {
          const groupName = (r.matId && r.matId > 0) ? (r.schoolName || '') : (r.matName || '');
          return groupName || '-';
        },
      },
    ];

    const billingColumns = [
      { key: 'orderDate', label: 'Order Date', render: (r) => r.orderDate },
      {
        key: 'product',
        label: 'Product',
        render: () => (
          <Stack direction='row' rowGap={0.5} columnGap={0.5} flexWrap='wrap'>
            <Chip label='Staff Wellbeing' className='staff-chip' size='small' />
            <Chip label='Staff Insight' className='student-chip' size='small' />
          </Stack>
        ),
      },
      { key: 'pupils', label: 'No. of Pupil', render: (r) => r.pupils },
      { key: 'generatedFrom', label: 'Generated From', render: (r) => r.generatedFrom },
      { key: 'totalPrice', label: 'Total Price', render: (r) => r.totalPrice },
      { key: 'discount', label: 'Discount', render: (r) => r.discount },
      {
        key: 'invoice',
        label: 'Invoice',
        render: () => (
          <IconButton disableRipple sx={{ p: 0 }}>
            <FileDownloadOutlinedIcon />
          </IconButton>
        ),
      },
    ];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          {loading===true? (
                <div className="center-section-loader">
                <CircularProgress />
                </div>
            ) : (
            <>
          {/* Account header cards */}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8} md={12} sm={12}>
              <Card>
                <CardHead>
                  <SchoolName>{selectedSchoolDetails.Name}</SchoolName>
                  <PillButton
                    type='button'
                    $variant='danger'
                    onClick={() => navigate.push(`/edit-school?schoolId=${schoolId}`)}
                  >
                    Edit School
                  </PillButton>
                </CardHead>
                <DetailList>
                  <DetailRow>
                    <DetailLabel>School URN:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.WondeeSchoolUrn}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Contact Email:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.Email}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Contact Number:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.ContactNumber}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Address:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.Address}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Number of Staff:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.NumberOfStaff}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Number of Pupils:</DetailLabel>
                    <DetailValue>{selectedSchoolDetails.NumberOfPupil}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Customer Type:</DetailLabel>
                    <DetailValue>{customerTypeLabel}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Wonde Sync Status:</DetailLabel>
                    <WondeConnectionPanel
                      schoolId={schoolId}
                      isMatLevel={false}
                    />
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Subscribe Products:</DetailLabel>
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
                      {productChips}
                    </Stack>
                  </DetailRow>
                </DetailList>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4} md={12} sm={12}>
              <Stack spacing={1.5}>
                <SubscriptionCard>
                  <CardHead>
                    <CardTitle>Subscription Detail</CardTitle>
                    <EditLink type='button' onClick={() => handleSchoolSubscriptionDetails(selectedSchoolDetails.Id)}>
                      Edit
                    </EditLink>
                  </CardHead>
                  <SubBlock>
                    <p>
                      <DetailValue>Subscription Term : {sub?.SubscriptionTerm || 0} years</DetailValue> <Muted>(Billed yearly)</Muted>
                    </p>
                    <p>
                      <DetailValue>Subscription Cost</DetailValue>
                    </p>
                    {term === 1 ? (
                      <p>
                        <DetailValue>Year 1 : £{yearCostWithVat(sub?.Year1Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                      </p>
                    ) : term === 2 ? (
                      <>
                        <p>
                          <DetailValue>Year 1 : £{yearCostWithVat(sub?.Year1Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                        </p>
                        <p>
                          <DetailValue>Year 2 : £{yearCostWithVat(sub?.Year2Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                        </p>
                      </>
                    ) : term === 3 ? (
                      <>
                        <p>
                          <DetailValue>Year 1 : £{yearCostWithVat(sub?.Year1Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                        </p>
                        <p>
                          <DetailValue>Year 2 : £{yearCostWithVat(sub?.Year2Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                        </p>
                        <p>
                          <DetailValue>Year 3 : £{yearCostWithVat(sub?.Year3Cost)}</DetailValue> <Muted>({vat}% VAT Applied)</Muted>
                        </p>
                      </>
                    ) : (
                      <p><DetailValue>£0</DetailValue></p>
                    )}
                    <p>
                      <Muted>Your License runs from </Muted>
                      <DateStart>{sub?.StartDate ? formatDate(sub.StartDate) : 'N/A'}</DateStart>
                      <Muted> - </Muted>
                      <DateEnd>{sub?.EndDate ? formatDate(sub.EndDate) : 'N/A'}</DateEnd>
                    </p>
                  </SubBlock>
                </SubscriptionCard>

                <SeatCard
                  entityType="school"
                  entityId={selectedSchoolDetails.Id}
                  assignedSeats={selectedSchoolDetails.AssignedSeats}
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Account views: AI Usage takes precedence, with the surveys and
              billing history relocated under the Survey Info tab. */}
          <AccountTabs
            idBase='school-account'
            defaultTabKey='ai-usage'
            tabs={[
              {
                key: 'ai-usage',
                label: 'AI Usage & Credits',
                content: <AiUsageSection scope='school' schoolId={schoolId} />,
              },
              {
                key: 'user-management',
                label: 'Access Management',
                content: (
                  <UserManagementSection
                    scope='school'
                    schoolId={schoolId}
                    parentGroupId={
                      // The school's parent MAT is the canonical top-level MatGroupId (null for a
                      // standalone school); fall back to a product's MAT id if that's absent.
                      Number(
                        selectedSchoolDetails?.MatGroupId ||
                          (selectedSchoolDetails?.schoolProductsList || [])
                            .map((p) => p?.MatGroupId ?? p?.MatId)
                            .find((id) => Number(id) > 0)
                      ) || undefined
                    }
                  />
                ),
              },
              {
                key: 'survey-info',
                label: 'Survey Info',
                content: (
                  <PageStack>
                    {/* Surveys */}
                    <Card>
                      <SectionHeaderRow title='Surveys' />
                      <FilterRow>
                        <SearchForm
                          onSubmit={(e) => {
                            e.preventDefault();
                            // Only search if 3 or more characters, or if empty
                            if (surveysSearchTerm.length === 0 || surveysSearchTerm.length >= 3) {
                              setSurveysPage(0);
                              loadSurveys(1, surveysRowsPerPage, surveysOrderBy, surveysOrder, surveysSearchTerm, surveysStatusFilter);
                            }
                          }}
                        >
                          <SearchIcon />
                          <input
                            placeholder='Search surveys...'
                            value={surveysSearchTerm}
                            onChange={handleSurveySearchChange}
                            aria-label='search surveys'
                          />
                        </SearchForm>
                        <FilterGroup>
                          <FilterLabel>Status</FilterLabel>
                          <SelectPill
                            value={surveysStatusFilter}
                            onChange={(value) => handleSurveyStatusFilterChange({ target: { value } })}
                            options={surveyStatusOptions}
                            ariaLabel='Status'
                            testId='survey-status-filter'
                          />
                        </FilterGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={surveysShowTesting}
                              onChange={handleSurveyShowTestingChange}
                              size='small'
                              sx={checkboxSx}
                            />
                          }
                          label='Show Testing Surveys'
                          sx={checkboxLabelSx}
                        />
                        <FilterGroup>
                          <FilterLabel>Audience</FilterLabel>
                          <SelectPill
                            value={surveysAudienceFilter}
                            onChange={(value) => handleSurveyAudienceFilterChange({ target: { value } })}
                            options={surveyAudienceOptions}
                            ariaLabel='Audience'
                            testId='survey-audience-filter'
                          />
                        </FilterGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={surveysCheckInFilter}
                              onChange={handleSurveyCheckInFilterChange}
                              size='small'
                              sx={checkboxSx}
                            />
                          }
                          label='Check-in Surveys Only'
                          sx={checkboxLabelSx}
                        />
                      </FilterRow>
                      {surveysLoading && (
                        <div
                          className="d-flex justify-center align-center p-20"
                        >
                          <CircularProgress size={24} />
                        </div>
                      )}
                      <DataTable
                        columns={surveyColumns}
                        rows={surveys}
                        getRowKey={(r) => r.id}
                        empty='No surveys found for this school.'
                      />
                      <PaginationRow>
                        <TablePagination
                          rowsPerPageOptions={[10, 20, 30]}
                          component='div'
                          count={surveysTotalCount}
                          rowsPerPage={surveysRowsPerPage}
                          page={surveysPage}
                          onPageChange={handleSurveyChangePage}
                          onRowsPerPageChange={handleSurveyChangeRowsPerPage}
                        />
                      </PaginationRow>
                    </Card>

                    {/* Billing History */}
                    <Card>
                      <SectionHeaderRow title='Billing History' />
                      <div style={{ marginTop: '16px' }}>
                        <DataTable
                          columns={billingColumns}
                          rows={billingRows}
                          getRowKey={(r) => r.id}
                        />
                      </div>
                    </Card>
                  </PageStack>
                ),
              },
            ]}
          />
            </>
          )}


        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

export default withRouter(SchoolDetail);
